import { rem } from '@mantine/core';
import type { TagKind } from '@reditelji/shared';
import { useState } from 'react';
import { useTagMutations, type TagRow } from '../api/tags';
import { useVolunteerMutations, type VolunteerRow } from '../api/volunteers';
import { useCommitField } from '../lib/draft';
import { applyDelta, tagMark, toggleDelta, type TagDelta, type TagTarget } from './tagLogic';
import { INPUT, MONO, TAG_TONES, seg, tagTone } from './ui';

/** What differs between the volunteer and the post vocabularies on screen. */
const TAG_COPY: Record<TagKind, { empty: string; removal: (n: number) => string }> = {
  volunteer: {
    empty: 'Še ni oznak — ustvari jih pri osebi z gumbom »+ Oznaka«.',
    removal: (n) => (n ? `z ${n} oseb` : 'iz sistema'),
  },
  post: {
    empty: 'Še ni oznak — ustvari jih pri mestu z gumbom »+ Oznaka«.',
    removal: (n) => (n === 1 ? 'z 1 mesta' : n ? `z ${n} mest` : 'iz sistema'),
  },
};

/**
 * A tag's name in the tag manager, saved when the coordinator leaves it. An
 * empty name is refused and the stored one comes back; a failed save keeps
 * the text and marks the field.
 */
function TagLabelInput({ tag, save }: { tag: TagRow; save: (label: string) => Promise<unknown> }) {
  const { state, inputProps } = useCommitField({ value: tag.label, save, accept: (text) => text !== '' });
  return (
    <input
      {...inputProps}
      aria-label={`Ime oznake ${tag.label}`}
      title={state === 'error' ? 'Ni shranjeno — popravi ali poskusi znova.' : undefined}
      aria-invalid={state === 'error' || undefined}
      style={{
        ...INPUT,
        flex: 1,
        minWidth: 0,
        padding: '5px 8px',
        background: 'oklch(0.99 0.002 95)',
        color: 'oklch(0.25 0.012 90)',
        borderColor: state === 'error' ? 'oklch(0.55 0.14 25)' : undefined,
        opacity: state === 'saving' ? 0.7 : 1,
      }}
    />
  );
}

/** Small round tag chip (list rows, tag bar). */
export function TagChip({
  tag,
  onRemove,
  small,
}: {
  tag: TagRow;
  onRemove?: () => void;
  small?: boolean;
}) {
  const tone = tagTone(tag.colour);
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: small ? 4 : 6,
        fontSize: small ? rem(11) : rem(12.5),
        padding: small ? '1px 6px' : onRemove ? '4px 6px 4px 10px' : '4px 10px',
        borderRadius: 20,
        background: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.bd}`,
        whiteSpace: 'nowrap',
      }}
    >
      {tag.scope === 'private' ? (
        <span title="osebna oznaka — vidiš jo samo ti" style={{ fontSize: rem(11), opacity: 0.8 }}>
          ◍
        </span>
      ) : null}
      <span>{tag.label}</span>
      {onRemove ? (
        <button
          onClick={onRemove}
          title="odstrani oznako"
          style={{
            cursor: 'pointer',
            width: 15,
            height: 15,
            borderRadius: '50%',
            border: 0,
            background: 'oklch(1 0 0 / 0.6)',
            color: tone.fg,
            fontSize: rem(11),
            lineHeight: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          ✕
        </button>
      ) : null}
    </span>
  );
}

/**
 * Tag picker dropdown: search, shared/private sections, toggle on the targets,
 * create new tag with colour + scope from the query. What a toggle writes is
 * the caller's, handed over as a delta.
 */
export function TagPicker({
  kind,
  tags,
  targets,
  onApply,
  onClose,
  style,
}: {
  kind: TagKind;
  tags: TagRow[];
  targets: TagTarget[];
  onApply: (delta: TagDelta) => void;
  onClose: () => void;
  style?: React.CSSProperties;
}) {
  const tagMut = useTagMutations(kind);
  const [q, setQ] = useState('');
  const [colourIdx, setColourIdx] = useState(0);
  const [scope, setScope] = useState<'shared' | 'private'>('shared');

  const query = q.trim().toLowerCase();
  const visible = tags.filter((t) => !query || t.label.toLowerCase().includes(query));
  const shared = visible.filter((t) => t.scope === 'shared');
  const priv = visible.filter((t) => t.scope === 'private');
  const exact = tags.some((t) => t.label.toLowerCase() === query);

  const markFor = (t: TagRow) => tagMark(targets, t.id);
  const toggle = (t: TagRow) => onApply(toggleDelta(targets, t.id));
  const create = () => {
    const label = q.trim();
    if (!label) return;
    tagMut.create.mutate(
      { label, colour: TAG_TONES[colourIdx]!.dot, scope },
      {
        onSuccess: (created) => {
          onApply({ add: [created.id], remove: [] });
          setQ('');
        },
      },
    );
  };

  const sectionRows = (list: TagRow[]) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {list.map((t) => {
        const tone = tagTone(t.colour);
        const mark = markFor(t);
        return (
          <button
            key={t.id}
            onClick={() => toggle(t)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              cursor: 'pointer',
              padding: '3px 9px',
              borderRadius: 20,
              fontSize: rem(12),
              fontFamily: 'inherit',
              background: mark === '✓' ? tone.bg : 'oklch(1 0 0)',
              color: mark === '✓' ? tone.fg : 'oklch(0.45 0.012 90)',
              border: `1px solid ${mark === '✓' ? tone.bd : 'oklch(0.92 0.006 95)'}`,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone.dot, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap' }}>{t.label}</span>
            {mark ? <span style={{ fontSize: rem(11), fontWeight: 700 }}>{mark}</span> : null}
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      style={{
        zIndex: 20,
        background: 'oklch(1 0 0)',
        border: '1px solid oklch(0.9 0.006 95)',
        borderRadius: 10,
        boxShadow: '0 8px 26px oklch(0.3 0.02 90 / 0.16)',
        padding: 11,
        ...style,
      }}
    >
      <input
        value={q}
        onChange={(e) => setQ(e.currentTarget.value)}
        placeholder="Išči ali ustvari oznako…"
        autoFocus
        style={{ ...INPUT, width: '100%', boxSizing: 'border-box', background: 'oklch(0.99 0.002 95)', fontSize: rem(13) }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 9, maxHeight: 220, overflow: 'auto' }}>
        {shared.length ? (
          <div>
            <div style={{ fontSize: rem(11), letterSpacing: '0.06em', fontWeight: 600, color: 'oklch(0.62 0.012 90)', padding: '0 2px 5px' }}>
              SKUPNE
            </div>
            {sectionRows(shared)}
          </div>
        ) : null}
        {priv.length ? (
          <div>
            <div style={{ fontSize: rem(11), letterSpacing: '0.06em', fontWeight: 600, color: 'oklch(0.62 0.012 90)', padding: '0 2px 5px' }}>
              MOJE OSEBNE ◍
            </div>
            {sectionRows(priv)}
          </div>
        ) : null}
      </div>
      {query && !exact ? (
        <div
          style={{
            borderTop: '1px dashed oklch(0.91 0.006 95)',
            marginTop: 10,
            paddingTop: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {TAG_TONES.map((t, i) => (
              <span
                key={t.dot}
                onClick={() => setColourIdx(i)}
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: t.dot,
                  cursor: 'pointer',
                  boxShadow: colourIdx === i ? `0 0 0 2px oklch(1 0 0), 0 0 0 3.5px ${t.dot}` : 'none',
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {(
              [
                ['shared', 'skupna'],
                ['private', 'samo zame'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setScope(value)}
                style={{
                  cursor: 'pointer',
                  padding: '3px 9px',
                  borderRadius: 20,
                  fontSize: rem(11.5),
                  fontFamily: 'inherit',
                  ...seg(scope === value),
                }}
              >
                {label}
              </button>
            ))}
            <div style={{ flex: 1 }} />
            <button
              onClick={create}
              style={{
                cursor: 'pointer',
                padding: '5px 11px',
                borderRadius: 6,
                fontSize: rem(12),
                fontWeight: 500,
                fontFamily: 'inherit',
                border: 0,
                background: 'oklch(0.35 0.05 245)',
                color: 'oklch(1 0 0)',
                whiteSpace: 'nowrap',
              }}
            >
              Ustvari »{q.trim()}«
            </button>
          </div>
        </div>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 9 }}>
        <button
          onClick={onClose}
          style={{
            cursor: 'pointer',
            padding: '4px 10px',
            borderRadius: 6,
            fontSize: rem(12),
            fontFamily: 'inherit',
            border: '1px solid oklch(0.91 0.006 95)',
            background: 'oklch(1 0 0)',
            color: 'oklch(0.45 0.012 90)',
          }}
        >
          Zapri
        </button>
      </div>
    </div>
  );
}

/** Tag manager overlay: rename, recolour, scope flip, delete with confirm. */
export function TagManager({
  kind,
  tags,
  usage,
  onClose,
}: {
  kind: TagKind;
  tags: TagRow[];
  usage: Map<string, number>;
  onClose: () => void;
}) {
  const mut = useTagMutations(kind);
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 40,
        background: 'oklch(0.3 0.02 90 / 0.28)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '48px 20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 560,
          maxHeight: '100%',
          overflow: 'auto',
          background: 'oklch(1 0 0)',
          borderRadius: 13,
          boxShadow: '0 18px 48px oklch(0.3 0.02 90 / 0.24)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '15px 18px 13px',
            borderBottom: '1px solid oklch(0.94 0.005 95)',
            position: 'sticky',
            top: 0,
            background: 'oklch(1 0 0)',
            zIndex: 2,
          }}
        >
          <span style={{ fontSize: rem(15), fontWeight: 600 }}>Oznake</span>
          <span style={{ fontSize: rem(12.5), color: 'oklch(0.58 0.012 90)', whiteSpace: 'nowrap' }}>
            preimenuj, prebarvaj, izbriši
          </span>
          <div style={{ flex: 1 }} />
          <button
            onClick={onClose}
            style={{
              cursor: 'pointer',
              width: 26,
              height: 26,
              borderRadius: 7,
              border: '1px solid oklch(0.91 0.006 95)',
              background: 'oklch(1 0 0)',
              color: 'oklch(0.45 0.012 90)',
              fontSize: rem(13),
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: '12px 18px 20px', display: 'flex', flexDirection: 'column', gap: 7 }}>
          {tags.length === 0 ? (
            <div style={{ fontSize: rem(13), color: 'oklch(0.6 0.012 90)', padding: '8px 2px' }}>
              {TAG_COPY[kind].empty}
            </div>
          ) : null}
          {tags.map((t) => {
            const tone = tagTone(t.colour);
            const used = usage.get(t.id) ?? 0;
            const conf = confirming === t.id;
            return (
              <div
                key={t.id}
                style={{
                  border: `1px solid ${conf ? 'oklch(0.88 0.05 25)' : 'oklch(0.93 0.006 95)'}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  background: 'oklch(1 0 0)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: tone.dot, flexShrink: 0 }} />
                  <TagLabelInput
                    tag={t}
                    save={(label) => mut.update.mutateAsync({ id: t.id, label })}
                  />
                  <span
                    style={{
                      fontSize: rem(11),
                      padding: '2px 8px',
                      borderRadius: 20,
                      background: t.scope === 'private' ? 'oklch(0.96 0.02 285)' : 'oklch(0.96 0.005 95)',
                      color: t.scope === 'private' ? 'oklch(0.42 0.07 285)' : 'oklch(0.5 0.012 90)',
                      flexShrink: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.scope === 'private' ? 'osebna ◍' : 'skupna'}
                  </span>
                  <span style={{ fontFamily: MONO, fontSize: rem(11.5), color: 'oklch(0.58 0.012 90)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {used}×
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 9 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {TAG_TONES.map((sw) => (
                      <span
                        key={sw.dot}
                        onClick={() =>
                          sw.dot !== t.colour &&
                          mut.update.mutate({ id: t.id, colour: sw.dot })
                        }
                        style={{
                          width: 13,
                          height: 13,
                          borderRadius: '50%',
                          background: sw.dot,
                          cursor: 'pointer',
                          boxShadow:
                            sw.dot === t.colour
                              ? `0 0 0 2px oklch(1 0 0), 0 0 0 3.5px ${sw.dot}`
                              : 'none',
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ flex: 1 }} />
                  <button
                    onClick={() =>
                      mut.update.mutate({
                        id: t.id,
                        scope: t.scope === 'private' ? 'shared' : 'private',
                      })
                    }
                    style={{
                      cursor: 'pointer',
                      padding: '4px 9px',
                      borderRadius: 6,
                      fontSize: rem(11.5),
                      fontFamily: 'inherit',
                      border: '1px solid oklch(0.92 0.006 95)',
                      background: 'oklch(1 0 0)',
                      color: 'oklch(0.45 0.012 90)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.scope === 'private' ? 'Naredi skupno' : 'Naredi osebno'}
                  </button>
                  {conf ? (
                    <>
                      <button
                        onClick={() => setConfirming(null)}
                        style={{
                          cursor: 'pointer',
                          padding: '4px 9px',
                          borderRadius: 6,
                          fontSize: rem(11.5),
                          fontFamily: 'inherit',
                          border: '1px solid oklch(0.92 0.006 95)',
                          background: 'oklch(1 0 0)',
                          color: 'oklch(0.45 0.012 90)',
                        }}
                      >
                        Prekliči
                      </button>
                      <button
                        onClick={() => {
                          mut.remove.mutate(t.id);
                          setConfirming(null);
                        }}
                        style={{
                          cursor: 'pointer',
                          padding: '4px 10px',
                          borderRadius: 6,
                          fontSize: rem(11.5),
                          fontWeight: 600,
                          fontFamily: 'inherit',
                          border: 0,
                          background: 'oklch(0.55 0.14 25)',
                          color: 'oklch(1 0 0)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Izbriši zdaj
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirming(t.id)}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 9px',
                        borderRadius: 6,
                        fontSize: rem(11.5),
                        fontFamily: 'inherit',
                        border: '1px solid oklch(0.91 0.03 25)',
                        background: 'oklch(1 0 0)',
                        color: 'oklch(0.5 0.11 25)',
                      }}
                    >
                      Izbriši
                    </button>
                  )}
                </div>
                {conf ? (
                  <div
                    style={{
                      marginTop: 8,
                      padding: '7px 9px',
                      borderRadius: 7,
                      background: 'oklch(0.985 0.016 25)',
                      border: '1px solid oklch(0.91 0.04 25)',
                      fontSize: rem(12),
                      color: 'oklch(0.45 0.08 25)',
                      lineHeight: 1.4,
                    }}
                  >
                    Oznaka bo odstranjena {TAG_COPY[kind].removal(used)}. Tega ni mogoče
                    razveljaviti.
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * The tags in use as filter chips, with their counts. A selected tag keeps its
 * chip even when nothing carries it any more, so a filter that empties the list
 * can always be taken off again.
 */
export function TagFilterChips({
  tags,
  usage,
  filter,
  onToggle,
  onClear,
}: {
  tags: TagRow[];
  usage: Map<string, number>;
  filter: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const shown = tags.filter((t) => (usage.get(t.id) ?? 0) > 0 || filter.includes(t.id));
  if (!shown.length) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', paddingTop: 1 }}>
      {shown.map((t) => {
        const tone = tagTone(t.colour);
        const on = filter.includes(t.id);
        return (
          <button
            key={t.id}
            onClick={() => onToggle(t.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              cursor: 'pointer',
              padding: '3px 8px',
              borderRadius: 20,
              fontSize: rem(11.5),
              fontFamily: 'inherit',
              background: on ? tone.bg : 'oklch(1 0 0)',
              color: on ? tone.fg : 'oklch(0.45 0.012 90)',
              border: `1px solid ${on ? tone.bd : 'oklch(0.92 0.006 95)'}`,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone.dot, flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap' }}>{t.label}</span>
            {t.scope === 'private' ? (
              <span title="osebna oznaka — vidiš jo samo ti" style={{ fontSize: rem(11), opacity: 0.7 }}>
                ◍
              </span>
            ) : null}
            <span style={{ fontFamily: MONO, fontSize: rem(11), opacity: 0.65 }}>{usage.get(t.id) ?? 0}</span>
          </button>
        );
      })}
      {filter.length > 0 ? (
        <button
          onClick={onClear}
          style={{
            cursor: 'pointer',
            padding: '3px 8px',
            borderRadius: 20,
            fontSize: rem(11.5),
            fontFamily: 'inherit',
            background: 'oklch(1 0 0)',
            color: 'oklch(0.55 0.012 90)',
            border: '1px dashed oklch(0.88 0.008 95)',
          }}
        >
          počisti
        </button>
      ) : null}
    </div>
  );
}

/** Up to three small chips and a "+N" — how a list row shows its tags. */
export function RowTagChips({ tags }: { tags: TagRow[] }) {
  if (!tags.length) return null;
  return (
    <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
      {tags.slice(0, 3).map((t) => {
        const tone = tagTone(t.colour);
        return (
          <span
            key={t.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: rem(11),
              padding: '1px 6px',
              borderRadius: 20,
              background: tone.bg,
              color: tone.fg,
              border: `1px solid ${tone.bd}`,
              whiteSpace: 'nowrap',
            }}
          >
            {t.scope === 'private' ? (
              <span title="osebna oznaka" style={{ fontSize: rem(11), opacity: 0.75 }}>
                ◍
              </span>
            ) : null}
            <span>{t.label}</span>
          </span>
        );
      })}
      {tags.length > 3 ? (
        <span style={{ fontSize: rem(11), color: 'oklch(0.62 0.012 90)', padding: '1px 3px' }}>
          +{tags.length - 3}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A picker delta applied to volunteers: one full-set write per person whose
 * tags it actually changes.
 */
export function useApplyVolunteerTags() {
  const { setTags } = useVolunteerMutations();
  return (targets: VolunteerRow[], delta: TagDelta) => {
    for (const v of targets) {
      const next = applyDelta(v.tagIds, delta);
      if (next) setTags.mutate({ id: v.id, tagIds: next });
    }
  };
}
